import { apiClient, handleApiError } from './client';
import type {
  ServiceDefinition,
  ServiceSuggestionsByCategory,
  ShortlistSummary,
  ShortlistItem,
  SaveShortlistRequest,
  CoverageWarning,
  ServiceDiscoveryResult,
} from '@om/shared-types';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
};

const unwrapApiResponse = <T>(payload: unknown, fallbackMessage: string): T => {
  if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
    const envelope = payload as ApiEnvelope<T>;
    if (envelope.success) {
      return envelope.data as T;
    }

    const message =
      (typeof envelope.error === 'string' && envelope.error.trim())
      || (typeof envelope.message === 'string' && envelope.message.trim())
      || fallbackMessage;
    throw new Error(message);
  }

  return payload as T;
};

class ServiceSuggestionApiService {
  async getSuggestions(celebrationId: number): Promise<ServiceSuggestionsByCategory[]> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/service-suggestions`);
      const payload = unwrapApiResponse<ServiceSuggestionsByCategory[]>(
        response.data,
        'Failed to load service suggestions'
      );
      if (!Array.isArray(payload)) {
        throw new Error('Invalid service suggestions response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getShortlist(celebrationId: number): Promise<ShortlistSummary> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/service-shortlist`);
      const payload = unwrapApiResponse<ShortlistSummary>(response.data, 'Failed to load shortlist');
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid shortlist response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async saveShortlist(celebrationId: number, req: SaveShortlistRequest): Promise<ShortlistItem[]> {
    try {
      const response = await apiClient.put(`/celebrations/${celebrationId}/service-shortlist`, req);
      const payload = unwrapApiResponse<ShortlistItem[]>(response.data, 'Failed to save shortlist');
      if (!Array.isArray(payload)) {
        throw new Error('Invalid shortlist save response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async finalizeShortlist(celebrationId: number): Promise<ShortlistSummary> {
    try {
      const response = await apiClient.post(`/celebrations/${celebrationId}/service-shortlist/finalize`);
      const payload = unwrapApiResponse<ShortlistSummary>(response.data, 'Failed to finalize shortlist');
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid shortlist finalize response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getShortlistSummary(celebrationId: number): Promise<ShortlistSummary> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/service-shortlist/summary`);
      const payload = unwrapApiResponse<ShortlistSummary>(response.data, 'Failed to load shortlist summary');
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid shortlist summary response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getCoverageWarnings(celebrationId: number): Promise<CoverageWarning[]> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/service-shortlist/coverage-warnings`);
      const payload = unwrapApiResponse<CoverageWarning[]>(response.data, 'Failed to load coverage warnings');
      if (!Array.isArray(payload)) {
        throw new Error('Invalid coverage warnings response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async searchDefinitions(category: string, query: string): Promise<ServiceDiscoveryResult> {
    try {
      const params = new URLSearchParams({ category, q: query });
      const response = await apiClient.get(`/service-definitions/search?${params.toString()}`);
      const payload = unwrapApiResponse<ServiceDiscoveryResult>(response.data, 'Failed to search services');
      if (!payload || typeof payload !== 'object' || !Array.isArray(payload.definitions)) {
        throw new Error('Invalid service discovery response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateServiceDetails(
    celebrationId: number,
    itemId: number,
    details: {
      budget?: number | null;
      assigned_to?: string | null;
      notes?: string | null;
      applies_to_event_ids?: number[] | null;
      custom_field_values?: Record<string, string> | null;
      request_name?: string | null;
    },
  ): Promise<ShortlistItem> {
    try {
      const response = await apiClient.patch(
        `/celebrations/${celebrationId}/service-shortlist/${itemId}/details`,
        details,
      );
      const payload = unwrapApiResponse<ShortlistItem>(response.data, 'Failed to update service details');
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid service details response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createAdditionalRequest(
    celebrationId: number,
    itemId: number,
    requestName?: string,
  ): Promise<ShortlistItem> {
    try {
      const response = await apiClient.post(
        `/celebrations/${celebrationId}/service-shortlist/${itemId}/new-request`,
        requestName ? { request_name: requestName } : {},
      );
      const payload = unwrapApiResponse<ShortlistItem>(response.data, 'Failed to create additional request');
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid additional request response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getCelebrationServiceDefinitions(celebrationId: number): Promise<ServiceDefinition[]> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/service-definitions`);
      const payload = unwrapApiResponse<ServiceDefinition[]>(response.data, 'Failed to load service definitions');
      if (!Array.isArray(payload)) {
        throw new Error('Invalid service definitions response');
      }
      return payload;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export const serviceSuggestionApiService = new ServiceSuggestionApiService();
